import React, { useContext, useEffect, useRef, useState } from 'react'
import { IncomingCall } from '~/service/SipConn'
import { FormattedMessage } from '~/translations/utils'
import {
  AddressBook,
  AddressBookManagerContext,
} from '~/service/AddressBookManager'
import { formatPhoneNumber, shorten } from '~/utils/string'
import classNames from 'classnames'

type Props = {
  call: IncomingCall
}

const Redirect: React.FC<Props> = ({ call }) => {
  const { addressBooks, contacts } = useContext(AddressBookManagerContext)
  const [search, setSearch] = useState('')
  const [activeId, setActiveId] = useState<number>()
  const [redirecting, setRedirecting] = useState(false)

  const redirect = (numberStr: string) => {
    setRedirecting(true)

    call.sendDtmf('*')

    for (let i = 0; i < numberStr.length; i++) {
      setTimeout(() => {
        call.sendDtmf(numberStr.charAt(i))

        if (i == numberStr.length - 1) {
          setTimeout(() => {
            call.bye()
          }, 500)
        }
      }, 1000 + i * 300)
    }
  }

  useEffect(() => {
    setActiveId(undefined)
  }, [search])

  // file deepcode ignore reDOS: <please specify a reason of ignoring this>
  const regexp =
    search.length > 3 &&
    new RegExp(`(${search.removeAccents().escapeRegExp()})`, 'i')

  const searchNumber = search.replace(' ', '').replace(/[^0-9\+]/g, '')

  const active = contacts.find(contact => contact.id == activeId)

  return (
    <div className="phone-redirect">
      <div className="field">
        <p className="control has-icons-left has-icons-right">
          <input
            className="input is-rounded"
            type="text"
            placeholder={intl.formatMessage({
              id: 'phone / call / redirect / search',
            })}
            autoFocus={true}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="icon is-small is-left">
            <i className="far fa-search"></i>
          </span>
        </p>
      </div>

      <div className="list">
        {Object.values(addressBooks).length ? (
          Object.values(addressBooks).map((addressBook, k) => (
            <Items
              searchNumber={searchNumber}
              addressBook={addressBook}
              active={activeId}
              setActiveId={setActiveId}
              key={k}
              regexp={regexp}
            />
          ))
        ) : (
          <div className="list-empty">
            <FormattedMessage id="phone / call / redirect / empty" />
          </div>
        )}
      </div>

      {active ? (
        <Button loading={redirecting} onClick={() => redirect(active.number)}>
          <FormattedMessage id="phone / call / redirect / redirect" />{' '}
          {shorten(active.name, 15)} ({formatPhoneNumber(active.number)})
        </Button>
      ) : searchNumber.length == 9 ? (
        <Button loading={redirecting} onClick={() => redirect(searchNumber)}>
          <FormattedMessage id="phone / call / redirect / redirect" />{' '}
          {formatPhoneNumber(searchNumber)}
        </Button>
      ) : null}
    </div>
  )
}

export default Redirect

type ButtonProps = {
  loading: boolean
  onClick: () => void
  children: React.ReactNode
}

const Button: React.FC<ButtonProps> = ({ children, loading, onClick }) => {
  return (
    <div className="phone-redirect-button">
      <button
        disabled={loading}
        className={classNames('button is-success', { 'is-loading': loading })}
        onClick={() => onClick()}
      >
        {children}
      </button>
    </div>
  )
}

type ItemsProps = {
  searchNumber: string
  addressBook: AddressBook
  active: number | undefined
  setActiveId: React.Dispatch<React.SetStateAction<number | undefined>>
  regexp: false | RegExp
}

const Items: React.FC<ItemsProps> = ({
  addressBook,
  regexp,
  searchNumber,
  setActiveId,
  active,
}) => {
  const { contactsGrouped } = useContext(AddressBookManagerContext)
  const items = regexp
    ? addressBook.name.match(regexp)
      ? contactsGrouped[addressBook.id]
      : contactsGrouped[addressBook.id].filter(
          item =>
            item.name.match(regexp) ||
            (searchNumber.length && item.number.includes(searchNumber))
        )
    : contactsGrouped[addressBook.id]

  if (!items || !items.length) {
    return null
  }

  return (
    <>
      {addressBook.name.length > 0 && (
        <div className="list-items-header">{addressBook.name}</div>
      )}

      <div className="list-items">
        {items.map(contact => (
          <a
            href=""
            className={classNames('list-item', {
              'is-active': active == contact.id,
            })}
            onClick={e => {
              e.preventDefault()
              setActiveId(contact.id)
            }}
            key={contact.id}
          >
            {contact.name || formatPhoneNumber(contact.number)}
            {contact.internalNumber && `(${contact.internalNumber})`}
          </a>
        ))}
      </div>
    </>
  )
}
